import { ensureError, ensureHelp, type FieldType } from './base.js';
import { withCustomMessage } from './text.js';
import { t } from '../../i18n/index.js';

/**
 * Не переносим потенциально исполняемые URL из data-атрибута в настоящий
 * `href`. Обычные относительные адреса, якоря и http(s)-ссылки остаются
 * доступными — именно они нужны для документов и политик.
 */
function safeLinkUrl(raw: string | null): string {
  const value = raw?.trim() ?? '';
  if (!value) return '';
  const compact = [...value]
    .filter((character) => character.charCodeAt(0) > 0x20)
    .join('')
    .toLowerCase();
  const scheme = compact.match(/^([a-z][a-z0-9+.-]*):/);
  if (scheme && scheme[1] !== 'http' && scheme[1] !== 'https') return '';
  return value;
}

function renderLabelContent(ctx: Parameters<FieldType['render']>[0], text: HTMLElement): void {
  const label = ctx.label ?? '';
  const linkUrl = safeLinkUrl(ctx.host.getAttribute('link-url'));
  const explicitLinkLabel = ctx.host.getAttribute('link-label')?.trim() ?? '';

  text.replaceChildren();

  if (linkUrl) {
    // Если link-label совпадает с частью label, ссылка остаётся ровно в этом
    // месте фразы. Иначе label служит префиксом. Без link-label кликабельна
    // вся подпись.
    const linkPosition = explicitLinkLabel ? label.indexOf(explicitLinkLabel) : -1;
    const prefix =
      linkPosition >= 0 ? label.slice(0, linkPosition) : explicitLinkLabel ? label : '';
    const suffix = linkPosition >= 0 ? label.slice(linkPosition + explicitLinkLabel.length) : '';
    if (prefix) {
      text.append(document.createTextNode(prefix));
      if (linkPosition < 0 && !/\s$/.test(prefix)) text.append(document.createTextNode(' '));
    }
    const link = document.createElement('a');
    link.className = 'plp-field-link';
    link.href = linkUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = explicitLinkLabel || label || linkUrl;
    text.appendChild(link);
    if (suffix) text.append(document.createTextNode(suffix));
  } else {
    text.append(document.createTextNode(label));
  }

  if (ctx.required) {
    const star = document.createElement('span');
    star.className = 'plp-field-required';
    star.textContent = '*';
    text.appendChild(star);
  }
}

export const checkboxType: FieldType = {
  render(ctx) {
    let wrap = ctx.host.querySelector<HTMLLabelElement>(':scope > label.plp-field-checkbox');
    if (!wrap) {
      wrap = document.createElement('label');
      wrap.className = 'plp-field-checkbox';
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.className = 'plp-field-input';
      const text = document.createElement('span');
      text.className = 'plp-field-label-text';
      wrap.appendChild(input);
      wrap.appendChild(text);
      ctx.host.insertBefore(wrap, ctx.host.querySelector(':scope > .plp-field-error'));
    }
    const input = wrap.querySelector<HTMLInputElement>('input[type="checkbox"]')!;
    const text = wrap.querySelector<HTMLElement>('.plp-field-label-text')!;
    input.name = ctx.name;
    input.value = ctx.host.getAttribute('value') ?? 'on';
    if (ctx.defaultValue === 'true' || ctx.defaultValue === '1') input.defaultChecked = true;
    renderLabelContent(ctx, text);
    ensureError(ctx.host);
    ensureHelp(ctx.host, ctx.help);
  },

  getValue(ctx) {
    const input = ctx.host.querySelector<HTMLInputElement>('input[type="checkbox"]');
    if (!input) return '';
    return input.checked ? input.value : '';
  },

  validate(ctx) {
    if (ctx.required) {
      const input = ctx.host.querySelector<HTMLInputElement>('input[type="checkbox"]');
      if (!input?.checked) {
        return withCustomMessage({ valid: false, error: t('form.validation.consent') }, ctx);
      }
    }
    return { valid: true };
  },

  shouldSubmit(ctx) {
    const input = ctx.host.querySelector<HTMLInputElement>('input[type="checkbox"]');
    return Boolean(input?.checked);
  },

  setDisabled(ctx, disabled) {
    const input = ctx.host.querySelector<HTMLInputElement>('input[type="checkbox"]');
    if (input) input.disabled = disabled;
  },
};
